/**
 * Admin Console user management (DEV-3767, RBAC plan §3.6 / Phase 4).
 *
 * The console manages one company at a time — the active company from the nav
 * bar's `CompanyPicker` — and lists the users who hold an assignment in it.
 * Everything shown comes from the company-scoped Admin API, so a membership in
 * a company the caller does not administer is never displayed nor actionable.
 *
 * Controls the caller cannot use are hidden rather than disabled: the assign
 * dropdown only offers the caller's own companies, and the caller cannot
 * deactivate themselves. What the controls *do* is still decided by cloud-core
 * (`IsAdminAlways`, last-admin protection) — the hiding is UX, not authority.
 */
import React, { FC, memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAuth } from "Components/Authentication/AuthProvider";
import {
  fetchGetCompanies,
  getCompanies,
  getIsCompaniesLoaded,
} from "Slices/companies";
import {
  ASSIGNABLE_ROLES,
  AdminUser,
  AssignableRole,
} from "Utils/adminUsersAPI";

import { InviteUserForm } from "./InviteUserForm";
import { useAdminUsers } from "./useAdminUsers";
import {
  ActionButtonStyled,
  AdminConsoleHeaderStyled,
  AdminConsoleTitleStyled,
  AdminTableStyled,
  CompanyTagStyled,
  EmptyStateStyled,
  HeaderControlsStyled,
  InviteButtonStyled,
  RevokeButtonStyled,
  SearchInputStyled,
  SelectStyled,
  StatusBadgeStyled,
  UserCountStyled,
} from "./styles";

const isAssignableRole = (value: string | null): value is AssignableRole =>
  !!value && (ASSIGNABLE_ROLES as readonly string[]).includes(value);

const matchesSearch = (user: AdminUser, searchValue: string): boolean => {
  const needle = searchValue.trim().toLowerCase();
  if (!needle) return true;
  return [user.email, user.firstName, user.lastName]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(needle));
};

export const AdminConsole: FC = memo(() => {
  const dispatch = useDispatch();
  const { user: authUser, companyIds, activeCompanyId } = useAuth();
  const companies = useSelector(getCompanies);
  const isCompaniesLoaded = useSelector(getIsCompaniesLoaded);
  const {
    users,
    isLoading,
    isLoadingError,
    reload,
    invite,
    changeRole,
    setActive,
    assignCompany,
    revokeCompany,
  } = useAdminUsers();

  const [searchValue, setSearchValue] = useState<string>("");
  const [showInviteForm, setShowInviteForm] = useState<boolean>(false);

  // GET /companies — company names for the title, invite form and assign
  // dropdown; nothing else on this page loads the companies store.
  useEffect(() => {
    if (isCompaniesLoaded) return;
    dispatch(fetchGetCompanies());
  }, [isCompaniesLoaded]);

  const companyNameById = useMemo(
    () =>
      companies.reduce<Record<string, string>>(
        (names, { id, name }) => ({ ...names, [id]: name }),
        {}
      ),
    [companies]
  );

  // The Admin API already names every visible assignment, so it can fill in
  // names the legacy companies list does not cover.
  const assignmentNameById = useMemo(
    () =>
      users.reduce<Record<string, string>>(
        (names, user) =>
          user.companies.reduce(
            (userNames, { companyId, companyName }) =>
              companyName && !userNames[companyId]
                ? { ...userNames, [companyId]: companyName }
                : userNames,
            names
          ),
        {}
      ),
    [users]
  );

  const nameOf = (companyId: string, fallback?: string | null): string =>
    companyNameById[companyId] ||
    assignmentNameById[companyId] ||
    fallback ||
    companyId;

  const scopedUsers = useMemo(
    () =>
      users.filter((user) =>
        user.companies.some(
          (assignment) => assignment.companyId === activeCompanyId
        )
      ),
    [users, activeCompanyId]
  );

  const visibleUsers = useMemo(
    () => scopedUsers.filter((user) => matchesSearch(user, searchValue)),
    [scopedUsers, searchValue]
  );

  if (!activeCompanyId) {
    return (
      <EmptyStateStyled data-test-id="admin_no_company">
        Select a company to manage its users.
      </EmptyStateStyled>
    );
  }

  return (
    <>
      <AdminConsoleHeaderStyled>
        <AdminConsoleTitleStyled>
          User Management — {nameOf(activeCompanyId)}
          <UserCountStyled data-test-id="admin_user_count">
            {scopedUsers.length}
          </UserCountStyled>
        </AdminConsoleTitleStyled>
        <HeaderControlsStyled>
          <SearchInputStyled
            data-test-id="admin_search"
            placeholder="Search user"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <InviteButtonStyled
            data-test-id="admin_invite_button"
            onClick={() => setShowInviteForm(!showInviteForm)}
          >
            Invite User
          </InviteButtonStyled>
        </HeaderControlsStyled>
      </AdminConsoleHeaderStyled>

      {showInviteForm && (
        <InviteUserForm
          activeCompanyId={activeCompanyId}
          activeCompanyName={nameOf(activeCompanyId)}
          onInvite={invite}
          onClose={() => setShowInviteForm(false)}
        />
      )}

      {isLoading && (
        <EmptyStateStyled data-test-id="admin_loading">
          Loading users…
        </EmptyStateStyled>
      )}

      {!isLoading && isLoadingError && (
        <EmptyStateStyled data-test-id="admin_loading_error">
          Could not load users.{" "}
          <ActionButtonStyled data-test-id="admin_retry" onClick={reload}>
            Retry
          </ActionButtonStyled>
        </EmptyStateStyled>
      )}

      {!isLoading && !isLoadingError && visibleUsers.length === 0 && (
        <EmptyStateStyled data-test-id="admin_empty">
          No users in {nameOf(activeCompanyId)}.
        </EmptyStateStyled>
      )}

      {!isLoading && !isLoadingError && visibleUsers.length > 0 && (
        <AdminTableStyled data-test-id="admin_users_table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Companies</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((user) => {
              // The caller must not deactivate or demote themselves from the
              // console: losing your own admin mid-session is never what an
              // admin means to do, and cloud-core's last-admin protection
              // would reject most of these writes anyway.
              const isSelf =
                !!authUser?.email &&
                authUser.email.toLowerCase() === user.email.toLowerCase();
              const activeAssignment = user.companies.find(
                (assignment) => assignment.companyId === activeCompanyId
              );
              const roleValue = isAssignableRole(activeAssignment?.role ?? null)
                ? (activeAssignment?.role as AssignableRole)
                : "";
              const assignableCompanyIds = companyIds.filter(
                (companyId) =>
                  !user.companies.some(
                    (assignment) => assignment.companyId === companyId
                  )
              );

              return (
                <tr key={user.id} data-test-id={`admin_user_${user.id}`}>
                  <td>
                    {[user.firstName, user.lastName].filter(Boolean).join(" ")}
                  </td>
                  <td>{user.email}</td>
                  <td>
                    {isSelf ? (
                      <span data-test-id={`admin_role_label_${user.id}`}>
                        {activeAssignment?.role ?? "—"}
                      </span>
                    ) : (
                      <SelectStyled
                        data-test-id={`admin_role_${user.id}`}
                        value={roleValue}
                        onChange={(event) =>
                          changeRole(
                            user.id,
                            activeCompanyId,
                            event.target.value as AssignableRole
                          )
                        }
                      >
                        {!roleValue && (
                          <option value="" disabled>
                            {activeAssignment?.role || "No role"}
                          </option>
                        )}
                        {ASSIGNABLE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </SelectStyled>
                    )}
                  </td>
                  <td>
                    <StatusBadgeStyled
                      data-test-id={`admin_status_${user.id}`}
                      $active={user.isActive}
                    >
                      {user.isActive ? "Active" : "Deactivated"}
                    </StatusBadgeStyled>
                  </td>
                  <td>
                    {user.companies.map((assignment) => (
                      <CompanyTagStyled
                        key={assignment.companyId}
                        data-test-id={`admin_company_${user.id}_${assignment.companyId}`}
                      >
                        {nameOf(assignment.companyId, assignment.companyName)}
                        {!isSelf && (
                          <RevokeButtonStyled
                            data-test-id={`admin_revoke_${user.id}_${assignment.companyId}`}
                            title="Revoke company"
                            onClick={() =>
                              revokeCompany(user.id, assignment.companyId)
                            }
                          >
                            ×
                          </RevokeButtonStyled>
                        )}
                      </CompanyTagStyled>
                    ))}
                    {assignableCompanyIds.length > 0 && (
                      <SelectStyled
                        data-test-id={`admin_assign_${user.id}`}
                        value=""
                        onChange={(event) =>
                          event.target.value &&
                          assignCompany(user.id, event.target.value)
                        }
                      >
                        <option value="">Assign to company…</option>
                        {assignableCompanyIds.map((companyId) => (
                          <option key={companyId} value={companyId}>
                            {nameOf(companyId)}
                          </option>
                        ))}
                      </SelectStyled>
                    )}
                  </td>
                  <td>
                    {!isSelf && (
                      <ActionButtonStyled
                        data-test-id={`admin_toggle_active_${user.id}`}
                        onClick={() => setActive(user.id, !user.isActive)}
                      >
                        {user.isActive ? "Deactivate" : "Reactivate"}
                      </ActionButtonStyled>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </AdminTableStyled>
      )}
    </>
  );
});
